<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <div v-if="label" class="ms-label" :class="{ required: isRequired }">{{ label }}</div>

    <div class="multiselect" ref="multiselectRef" v-bind="filteredAttrs">
      <div
        class="ms-trigger"
        :class="{
          'is-open': isOpen,
          'has-error': Array.isArray(error) ? error.length > 0 : !!error,
        }"
        @click="handleTriggerClick">
        <div class="chips-wrap">
          <template v-if="!loading">
            <span v-for="val in modelValue" :key="String(val)" class="chip">
              {{ getLabelForValue(val) }}
              <button type="button" class="chip-remove" @mousedown.prevent @click.stop="removeItem(val)" tabindex="-1">
                <IconX size="11" />
              </button>
            </span>
          </template>

          <input
            v-if="isOpen"
            ref="inputRef"
            v-model="searchQuery"
            type="text"
            class="ms-input"
            :placeholder="modelValue.length === 0 ? placeholder : ''"
            @keydown.down.prevent="navigate('down')"
            @keydown.up.prevent="navigate('up')"
            @keydown.enter.prevent.stop="selectHighlighted"
            @keydown.backspace="handleBackspace"
            @keydown.esc.prevent="closeDropdown"
            @blur="handleInputBlur" />
          <span v-else-if="!loading && modelValue.length === 0" class="ms-placeholder">{{ placeholder }}</span>
        </div>

        <div v-if="loading" class="ms-spinner" aria-label="Loading">
          <div class="spinner-circle"></div>
        </div>
        <button
          v-else
          type="button"
          class="ms-chevron"
          @mousedown.prevent
          @click.stop="handleChevronToggle"
          tabindex="-1">
          <IconChevronDown class="chevron-icon" :class="{ 'rotate-180': isOpen }" size="16" />
        </button>
      </div>

      <transition name="fade">
        <div v-if="isOpen" class="ms-dropdown">
          <div v-if="loading && filteredItems.length === 0" class="loading-state">
            <div class="spinner"></div>
          </div>
          <ul v-else-if="filteredItems.length > 0" class="dropdown-list" role="listbox">
            <li
              v-for="(item, index) in filteredItems"
              :key="resolveValue(item)"
              class="dropdown-item"
              :class="{ 'is-highlighted': index === highlightedIndex, 'is-checked': isChecked(item) }"
              @mousedown.prevent="toggleItem(item)"
              @mouseenter="highlightedIndex = index"
              role="option">
              <span class="item-name">{{ resolveLabel(item) }}</span>
              <IconCheck v-if="isChecked(item)" class="check-icon" size="16" />
            </li>
          </ul>
          <div v-else class="empty-state">No results</div>
        </div>
      </transition>
    </div>

    <FormErrorList
      v-if="error && (Array.isArray(error) ? error.length > 0 : true)"
      :errors="Array.isArray(error) ? error : [error]" />

    <div v-if="$slots.hint" class="hint-text">
      <slot name="hint" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, onMounted, onUnmounted, useAttrs } from 'vue';
import { useDropdownManager } from '@/composables/useDropdownManager';
import { IconChevronDown, IconX, IconCheck } from '@tabler/icons-vue';
import { FormErrorList } from '@prospective/lithium';

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const filteredAttrs = computed(() => {
  const { class: _c, style: _s, ...rest } = attrs;
  return rest;
});

interface OptionItem {
  [key: string]: any;
}

const props = defineProps<{
  modelValue: any[];
  items: OptionItem[];
  label?: string;
  placeholder?: string;
  loading?: boolean;
  error?: string | string[] | null;
  isRequired?: boolean;
  getOptionLabel?: string | ((option: OptionItem) => string);
  getOptionValue?: string | ((option: OptionItem) => any);
  disableFiltering?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: any[]): void;
  (e: 'search', query: string): void;
}>();

const { isOpen, open: openDropdownManager, close: closeDropdownManager } = useDropdownManager();

const searchQuery = ref('');
const highlightedIndex = ref(-1);
const multiselectRef = ref<HTMLElement | null>(null);
const inputRef = ref<HTMLInputElement | null>(null);

const resolveLabel = (option: OptionItem): string => {
  if (!option) return '';
  if (typeof props.getOptionLabel === 'function') return props.getOptionLabel(option);
  const key = (props.getOptionLabel as string) || 'name';
  return option[key] ?? '';
};

const resolveValue = (option: OptionItem): any => {
  if (!option) return null;
  if (typeof props.getOptionValue === 'function') return props.getOptionValue(option);
  const key = (props.getOptionValue as string) || 'id';
  return option[key];
};

const labelMap = computed(() => {
  const map = new Map<string, string>();
  for (const item of props.items) {
    map.set(String(resolveValue(item)), resolveLabel(item));
  }
  return map;
});

const selectedValueSet = computed(() => new Set(props.modelValue.map((v) => String(v))));

const getLabelForValue = (val: any): string => labelMap.value.get(String(val)) ?? String(val);

const isChecked = (item: OptionItem): boolean => selectedValueSet.value.has(String(resolveValue(item)));

const filteredItems = computed(() => {
  if (props.disableFiltering || !searchQuery.value) return props.items;
  return props.items.filter((item) => resolveLabel(item).toLowerCase().includes(searchQuery.value.toLowerCase()));
});

const openDropdown = async () => {
  if (isOpen.value) return;
  openDropdownManager();
  highlightedIndex.value = -1;
  emit('search', searchQuery.value);
  await nextTick();
  inputRef.value?.focus();
};

const closeDropdown = () => {
  if (!isOpen.value) return;
  closeDropdownManager();
  searchQuery.value = '';
  highlightedIndex.value = -1;
};

const handleTriggerClick = () => {
  if (isOpen.value) inputRef.value?.focus();
  else openDropdown();
};

const handleChevronToggle = () => {
  if (isOpen.value) closeDropdown();
  else openDropdown();
};

const toggleItem = (item: OptionItem) => {
  const val = resolveValue(item);
  const current = [...props.modelValue];
  const idx = current.findIndex((v) => String(v) === String(val));
  if (idx === -1) current.push(val);
  else current.splice(idx, 1);
  emit('update:modelValue', current);
  searchQuery.value = '';
  nextTick(() => inputRef.value?.focus());
};

const removeItem = (val: any) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((v) => String(v) !== String(val))
  );
};

const navigate = (direction: 'up' | 'down') => {
  const total = filteredItems.value.length;
  if (total === 0) return;
  if (direction === 'down') {
    highlightedIndex.value = (highlightedIndex.value + 1) % total;
  } else {
    highlightedIndex.value = (highlightedIndex.value - 1 + total) % total;
  }
};

const selectHighlighted = () => {
  if (highlightedIndex.value >= 0 && highlightedIndex.value < filteredItems.value.length) {
    toggleItem(filteredItems.value[highlightedIndex.value]);
  }
};

const handleBackspace = () => {
  if (!searchQuery.value && props.modelValue.length > 0) {
    emit('update:modelValue', props.modelValue.slice(0, -1));
  }
};

const handleInputBlur = (event: FocusEvent) => {
  if (multiselectRef.value && !multiselectRef.value.contains(event.relatedTarget as Node)) {
    closeDropdown();
  }
};

const handleClickOutside = (event: MouseEvent) => {
  if (multiselectRef.value && !multiselectRef.value.contains(event.target as Node)) {
    closeDropdown();
  }
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));
</script>

<style scoped lang="scss">
.ms-label {
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  margin-bottom: 6px;
  color: #344054;
  user-select: none;

  &.required::after {
    content: ' *';
    color: #ef4444;
  }
}

.multiselect {
  position: relative;
  width: 100%;
}

.ms-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 40px;
  padding: 4px 8px 4px 12px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  box-sizing: border-box;
  box-shadow: 0px 1px 2px 0px #1018280d;
  gap: 4px;

  &:hover {
    border-color: var(--tblr-primary);
  }

  &.is-open {
    border-color: var(--tblr-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.has-error {
    border-color: #ef4444;

    &:hover,
    &.is-open {
      border-color: #ef4444;
    }

    &.is-open {
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  }
}

.chips-wrap {
  display: flex;
  flex-wrap: wrap;
  flex-grow: 1;
  align-items: center;
  gap: 4px;
  min-height: 30px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px 2px 8px;
  background-color: #f2f4f7;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  white-space: nowrap;
}

.chip-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  color: #667085;
  transition: color 0.15s ease;
  line-height: 1;

  &:hover {
    color: #344054;
  }
}

.ms-input {
  flex-grow: 1;
  min-width: 80px;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  font-size: 14px;
  background: transparent;
  color: #111827;
  height: 28px;
  font-family: var(--font-brand);
}

.ms-placeholder {
  color: #9ca3af;
  font-size: 14px;
}

.ms-spinner {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.spinner-circle {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: var(--tblr-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.ms-chevron {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.chevron-icon {
  width: 20px;
  height: 20px;
  color: #6b7280;
  transition: transform 0.2s ease;

  &.rotate-180 {
    transform: rotate(180deg);
  }
}

.ms-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-height: 250px;
  overflow-y: auto;
  padding: 0 0 7px 0;
  background-color: #ffffff;
  border: 1px solid var(--tblr-primary);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  box-shadow: 0px 8px 8px 0px #00000026;
  z-index: 50;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }
}

.dropdown-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  height: 42px;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &.is-highlighted {
    background-color: #f3f4f6;
  }

  &.is-checked {
    background-color: #eff6ff;
  }
}

.item-name {
  flex-grow: 1;
  font-size: 14px;
  color: #111827;
}

.check-icon {
  flex-shrink: 0;
  color: var(--tblr-primary);
}

.empty-state {
  padding: 16px 12px;
  font-size: 13px;
  color: #9ca3af;
  text-align: center;
}

.loading-state {
  padding: 24px 12px;
  text-align: center;
  color: #9ca3af;
}

.spinner {
  margin: 16px auto;
  width: 24px;
  height: 24px;
  border: 3px solid #e5e7eb;
  border-top-color: var(--tblr-primary);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.hint-text {
  margin-top: 6px !important;
  font-size: 12px;
  font-weight: 400;
  color: #475467;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
